/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useUserStore } from '@/stores/user';
import type { CourtGetAllParams } from '@/types/court.types';
import { fetchCourts, useCourts } from '@/services/courtService';
import type {
  ClientGetAllParams,
  ClientResponse,
  ContactGetAllParams,
  ContactResponseContactType,
} from '@/types/client.types';
import { fetchClient, fetchClients, fetchContacts, useClients, useContacts } from '@/services/clients';
import type { UserGetAllParams } from '@/types/user.types';
import { getAllUsers, useUsers } from '@/services/userService';
import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getAllServices } from './pe';
import { ServiceGetAllRequestBody } from '@/types/service.types';
import { removeNullValues } from '@/lib/utils';
import { getVendorContactsByVendorId, getVendors } from './vendorService';
import { VendorContactGetAllParams, VendorGetAllParams, VendorStatus } from '@/types/vendor.types';
import type { BillingGetAllQuerySchemaType, BillingItemBaseType } from '@/types/billing.types';
import { getBillingItems } from '@/services/invoiceService';
import type { ListPageResponse } from '@/types/types';
import { endSession, resetLogoutTimer } from '@/services/inactivityTimeOut';
import { useTeamGetAll } from './teamService';
import { TeamGetAllParams } from '@/types/team.types';
import { refreshAccessToken } from '@/services/authRefresh';

export const unAuthorizedAxios = axios.create();

axios.defaults.baseURL = import.meta.env.VITE_API_URL as string;
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  headers: {
    Authorization: string;
  };
}

export const initializeNetwork = () => {
  axios.interceptors.response.use(
    (response) => {
      resetLogoutTimer();
      return response;
    },
    async (error: AxiosError): Promise<any> => {
      const originalRequest = error.config as CustomAxiosRequestConfig;

      if (error?.response?.status === 401 && !originalRequest?._retry && !originalRequest.url?.includes('/auth/')) {
        originalRequest._retry = true;
        // Read now, not from a snapshot: the session may have ended since.
        const { refreshToken } = useUserStore.getState();
        if (!refreshToken) return Promise.reject(error);
        try {
          const newAccessToken = await refreshAccessToken(refreshToken);
          // Signed out while refreshing: don't bring the token back.
          if (useUserStore.getState().refreshToken !== refreshToken) return Promise.reject(error);

          useUserStore.setState({ accessToken: newAccessToken });

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          resetLogoutTimer();
          return axios(originalRequest);
        } catch (refreshError) {
          // Awaited: the full page load below would cancel the logout request.
          await endSession('SESSION_EXPIRED', { showExpiredMessage: true });
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  axios.interceptors.request.use((req) => {
    const currentAccessToken = useUserStore.getState().accessToken;
    req.headers = req.headers || {};
    if (currentAccessToken) {
      req.headers.Authorization = `Bearer ${currentAccessToken}`;
    }
    return req;
  });
};
initializeNetwork();
export const getClients = (params: ClientGetAllParams, options: any) => {
  const query = useClients(params, options);
  const data = query.data?.clients ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getProcesses = (body: ServiceGetAllRequestBody, options: any) => {
  const query = useQuery({
    queryFn: () => getAllServices(removeNullValues(body)),
    queryKey: ['services', options],
  });
  const data = query.data?.services ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getUsers = (params: UserGetAllParams, options: any) => {
  const query = useUsers(params, options);
  const data = query.data?.users ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getAllCourts = (params: CourtGetAllParams, options: any) => {
  const query = useCourts(params, options);
  const data = query.data?.courts ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const getAllVendors = (params: VendorGetAllParams, options?: { query?: UseQueryOptions }) => {
  const query = useQuery({
    queryFn: () => getVendors(params),
    queryKey: ['vendors', params],
    ...options,
  });
  const data = query.data?.data?.vendors ?? [];
  const totalRows = query?.data?.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};

export const getClientContactsById = (params: ContactGetAllParams, options: any) => {
  const query = useContacts(params, options);
  const data = query.data?.contacts ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};

export const getProcessesById = (params: UserGetAllParams, options: any) => {
  const query = useUsers(params, options);
  const data = query.data?.users ?? [];
  const totalRows = query.data?.totalRows ?? 0;
  return { ...query, data, totalRows };
};
export const clientSearch = (params: ClientGetAllParams, _options: any, refreshKey?: number) => {
  const query = useQuery({
    queryFn: () => fetchClients({ ...params, sortField: 'code', sortDirection: 'asc' }),
    queryKey: [
      'clients',
      Object.values(params)
        .map((value) => value)
        .join(', '),
      refreshKey,
    ],
    staleTime: 1000 * 60 * 5,
  });
  const data = query.data?.clients ?? [];
  const values = data.map((client) => ({
    displayLabel: client.code,
    label: client.code + ' - ' + client.clientName,
    secondaryLabel: client.city + ', ' + client.state,
    value: client.clientId,
    data: client,
  }));
  return { ...query, values, refetch: query.refetch };
};

export const clientGetById = (params: string, options?: { query?: Partial<UseQueryOptions<ClientResponse>> }) => {
  const query = useQuery<ClientResponse>({
    queryFn: () => fetchClient(params),
    queryKey: ['clients', params],
    staleTime: 1000 * 60 * 5,
    ...options?.query,
  });
  const rawData = query.data;
  const data: ClientResponse | null =
    rawData && typeof rawData === 'object' && 'clientId' in rawData
      ? rawData
      : null;

  return { ...query, data };
};

export const clientContactSearch = (
  params: ContactGetAllParams,
  _options: any,
  employeeType?: ContactResponseContactType,
  refreshKey?: number
) => {
  const query = useQuery({
    queryFn: () => fetchContacts({ ...params, sortField: 'firstName', sortDirection: 'asc' }),
    queryKey: ['contacts', params.search, params.clientId, refreshKey],
    staleTime: 1000 * 60 * 5,
    enabled: !!params.clientId,
  });

  let data = query.data?.contacts ?? [];
  if (employeeType) {
    data = data.filter((contact) => contact.type === employeeType);
  }
  const values = data.map((contact) => ({
    label: contact.firstName + ' ' + contact.lastName,
    value: contact.contactId ?? '',
    option: contact,
  }));
  return { ...query, values };
};

export const courtSearch = (params: CourtGetAllParams, _options: any, refreshKey?: number) => {
  const query = useQuery({
    queryFn: () => fetchCourts({ ...params, sortField: 'name', sortDirection: 'asc' }),
    queryKey: ['courts', params.search, refreshKey],
    staleTime: 1000 * 60 * 5,
  });

  const data = query.data?.courts ?? [];
  const values = data.map((court) => ({
    label: `${court.code} - ${court.name}`,
    value: court.courtId,
  }));
  return { ...query, values };
};

export const serverSearch = (params: UserGetAllParams) => {
  const query = useQuery({
    queryFn: () => getAllUsers({ ...params, roleId: 'SERVER', pageSize: 100, sortField: 'userName', sortDirection: 'asc' }),
    queryKey: ['servers', params.search],
    staleTime: 1000 * 60 * 5,
  });
  const data =
    query.data?.users?.filter((user) => user.status === 'ACTIVE') ?? [];
  const values = data.map((server) => ({
    label: server?.firstName + ' ' + server?.lastName,
    value: server.userId,
  }));
  return { ...query, values };
};

export const vendorSearch = (
  params: VendorGetAllParams,
  options?: { query?: UseQueryOptions },
  vendorId?: string | null
) => {
  const query = useQuery({
    queryFn: async () => {
      const sized: VendorGetAllParams = {
        ...params,
        pageSize: params.pageSize ?? 100,
        sortField: params.sortField ?? 'name',
        sortDirection: params.sortField ? params.sortDirection : 'asc',
      };
      const hadCoverage = Boolean(typeof sized.coverageArea === 'string' && sized.coverageArea.trim().length > 0);
      let response = await getVendors(sized);
      const firstCount = response.data?.vendors?.length ?? 0;
      if (hadCoverage && firstCount === 0) {
        const withoutCoverage: VendorGetAllParams = { ...sized };
        delete withoutCoverage.coverageArea;
        response = await getVendors(withoutCoverage);
      }
      return response;
    },
    queryKey: ['vendors', JSON.stringify(params)],
    staleTime: 1000 * 60 * 5,
    ...options?.query,
  });
  const data = query.data?.data.vendors ?? [];
  const filteredData = data.filter((vendor) => vendor.status === VendorStatus.ACTIVE || vendor?.vendorId === vendorId);
  const values = filteredData.map((vendor) => ({
    label: vendor.name,
    value: vendor.vendorId,
  }));
  return { ...query, values, refetch: query.refetch };
};

export const vendorContactSearch = (
  params: VendorContactGetAllParams,
  vendorId?: string | null,
  options?: { query?: UseQueryOptions }
) => {
  const query = useQuery({
    queryFn: () => getVendorContactsByVendorId(vendorId, params),
    queryKey: ['contacts', params.search, vendorId],
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 5,
    ...options?.query,
  });

  const data = query.data?.data ?? [];
  const values = data
    .filter((contact) => contact.vendorContactId !== undefined)
    .map((contact) => {
      const fromContactName = contact.contactName?.trim();
      const fromFirstLast = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
      const label = fromContactName ? fromContactName : fromFirstLast.length > 0 ? fromFirstLast : contact.email;
      return {
        label,
        value: contact.vendorContactId!,
      };
    });
  return { ...query, values };
};

export const billingItemSearch = (params: BillingGetAllQuerySchemaType, options: any) => {
  const query = useQuery<AxiosResponse<ListPageResponse<'billingItems', BillingItemBaseType>>>({
    queryFn: () => getBillingItems(params),
    queryKey: ['billing-items', Object.values(params).join(', ')],
    staleTime: 1000 * 60 * 5,
    ...options,
  });

  const values =
    query.data?.data?.billingItems?.map((item: BillingItemBaseType) => ({
      value: item.itemId ?? '',
      label: `${item.itemId} - ${item.description}`,
      option: item,
    })) ?? [];
  return { ...query, values };
};

export const teamSearch = (params: TeamGetAllParams) => {
  const normalizedParams = { ...params };
  const teamIdCandidate = normalizedParams.teamId?.trim();
  const isUuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  // FormFieldSearch can pass typed text in teamId; API expects teamId as UUID.
  if (teamIdCandidate) {
    if (isUuid(teamIdCandidate)) {
      normalizedParams.teamId = teamIdCandidate;
    } else {
      normalizedParams.search = teamIdCandidate;
      delete normalizedParams.teamId;
    }
  }

  const query = useTeamGetAll(normalizedParams);
  const data = query.data?.teams ?? [];

  const filteredData = data.filter((team) => !['ADMIN', 'PROCESS_SERVER'].includes(team.key));

  const values = filteredData?.map((team) => ({
    label: team.name,
    value: team.id,
  }));

  return { ...query, values };
};

export const getUsersByTeam = (params: TeamGetAllParams, isSupervisorOrAdmin: boolean) => {
  const query = useTeamGetAll(params);
  const currentUserId = useUserStore.getState().user?.userId;
  const teams = query.data?.teams ?? [];

  const allUsers = teams.flatMap(
    (team) =>
      team?.users?.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.userId,
      })) ?? []
  );

  const values = isSupervisorOrAdmin ? allUsers : allUsers.filter((user) => user.value === currentUserId);

  return { ...query, values };
};
